/**
 * Choosing which moment of a clip becomes its thumbnail.
 *
 * Split out from the upload dialog because the decision is the fiddly part and
 * it is worth testing on its own: seeking and decoding need a real browser, but
 * "given what these frames looked like, which one do we keep" does not.
 */

export interface Frame {
  /** The captured still, as a small JPEG data URI. */
  dataUrl: string;
  /** Mean luminance, 0-255. Only ever used to compare frames. */
  luminance: number;
}

/**
 * Where to look, as fractions of the clip, in order.
 *
 * The first frame is the worst possible choice: signage clips open on a fade
 * from black or a blank brand plate, so a still from t=0 is a black rectangle
 * that tells an operator nothing about which clip it is. Halfway is the best
 * single guess, but it is only a guess - a clip can cut to black at exactly
 * that point, between two segments. So these walk outward from the middle and
 * stay out of the first and last tenth, where leaders and end cards live.
 */
export const POSTER_FRACTIONS = [0.5, 0.35, 0.65, 0.2, 0.8, 0.1];

/**
 * Mean luminance under which a frame counts as "nothing to see".
 *
 * 12/255 passes a genuinely dim night shot while rejecting a fade-to-black or
 * a blank leader.
 */
export const BLACK_THRESHOLD = 12;

/**
 * The timestamps to try, in order.
 *
 * A stream, or a container the browser cannot length, reports a duration of
 * Infinity or 0. There is no "halfway" into something with no end, so that
 * falls back to a single early frame and accepts that it may be dark.
 */
export function posterPoints(duration: number): number[] {
  if (!Number.isFinite(duration) || duration <= 0) return [0.1];
  return POSTER_FRACTIONS.map((f) => duration * f);
}

/**
 * Walk the clip until a frame worth showing turns up.
 *
 * Stops at the first frame that is not essentially black, so the common case
 * costs exactly one seek. If every point is dark the clip really is dark, and
 * the brightest of them is kept - a dim thumbnail still identifies a clip,
 * whereas no thumbnail reads as a failed upload.
 *
 * `capture` returns null for a seek that landed nowhere or a frame that could
 * not be read; those are skipped rather than treated as black.
 */
export async function searchPoster(
  duration: number,
  capture: (at: number) => Promise<Frame | null>
): Promise<string | null> {
  let best: Frame | null = null;
  for (const at of posterPoints(duration)) {
    const frame = await capture(at);
    if (frame && (!best || frame.luminance > best.luminance)) best = frame;
    if (best && best.luminance >= BLACK_THRESHOLD) break;
  }
  return best?.dataUrl ?? null;
}
